import { useState, useCallback, useEffect } from 'react';
import { Warning } from '@phosphor-icons/react';
import { ConfirmationDialog } from './BaseDialog';

interface DeleteDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback function to be called when the dialog is closed */
  onClose: () => void;
  /** The title of the dialog */
  title?: string;
  /** The message to display in the dialog */
  message?: string;
  /** The item being deleted (for display purposes) */
  itemName?: string;
  /** The type of item being deleted (e.g., 'interview', 'job', 'client') */
  itemType?: string;
  /** Callback function to be called when deletion is confirmed */
  onConfirm: () => void | Promise<void>;
  /** Optional custom confirm button label */
  confirmLabel?: string;
  /** Optional custom cancel button label */
  cancelLabel?: string;
  /** Whether to show a warning icon */
  showWarning?: true;
}

/**
 * A reusable delete confirmation dialog component
 * This dialog provides a consistent interface for confirming deletions across the application
 */
export default function DeleteDialog({
  isOpen,
  onClose,
  title,
  message,
  itemName,
  itemType = 'item',
  onConfirm,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  showWarning = true
}: DeleteDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(false);
      setError(null);
    }
  }, [isOpen]);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await onConfirm();
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(`Failed to delete ${itemType}. ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm, onClose, itemType]);

  const dialogTitle = title || `Delete ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}`;

  const dialogMessage =
    message ||
    (itemName
      ? `Are you sure you want to delete "${itemName}"? This action cannot be undone.`
      : `Are you sure you want to delete this ${itemType}? This action cannot be undone.`);

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      title={dialogTitle}
      message={dialogMessage}
      onConfirm={handleConfirm}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      isDangerous
      isConfirmLoading={isLoading}
      error={error}
      titleIcon={showWarning ? <Warning size={32} color="#D92D20" aria-hidden="true" /> : undefined}
      closeOnInteractOutside={false}
    />
  );
}
